// ========================================
// TRANSFORMATEUR DE DONNÉES MOBILE-OPTIMISÉ
// ========================================

import type {
  FlexibleChartConfig,
  DataSourceConfig,
  AggregationType,
  TimeAggregation
} from '../types/customCharts'
import type {
  SalesByIntervalResponse,
  ProductsReportResponse
} from '../responses'
import { DateTime } from 'luxon'

// Interface pour les données brutes
export interface RawDataPoint {
  date: string
  periodType?: 'main' | 'comparison'
  periodLabel?: string
  [key: string]: any
}

// Interface pour les données transformées
export interface TransformedDataPoint {
  x: string | number | Date
  y: number
  label?: string
  color?: string
  metadata?: Record<string, any>
}

// Interface pour un dataset Chart.js mobile-optimisé
export interface ChartDataset {
  label: string
  data: TransformedDataPoint[]
  backgroundColor?: string | string[]
  borderColor?: string | string[] | undefined
  borderWidth?: number
  tension?: number
  fill?: boolean
  // Optimisations mobile
  pointRadius?: number
  pointHoverRadius?: number
  borderCapStyle?: string
}

// ========================================
// AGRÉGATIONS STATISTIQUES
// ========================================

export class StatisticalAggregator {

  static aggregate(data: number[], _type: AggregationType): number {
    if (data.length === 0) return 0

    // Filtrer les valeurs nulles/undefined
    const validData = data.filter(val => val !== null && val !== undefined && !isNaN(val))
    if (validData.length === 0) return 0
    return validData[0];
    // switch (type) {
    //   case 'sum':
    //     return validData.reduce((acc, val) => acc + val, 0)

    //   case 'count':
    //     return validData.length

    //   case 'average':
    //     return validData.reduce((acc, val) => acc + val, 0) / validData.length

    //   case 'median':
    //     const sorted = [...validData].sort((a, b) => a - b)
    //     const mid = Math.floor(sorted.length / 2)
    //     return sorted.length % 2 === 0
    //       ? (sorted[mid - 1] + sorted[mid]) / 2
    //       : sorted[mid]

    //   case 'max':
    //     return Math.max(...validData)

    //   case 'min':
    //     return Math.min(...validData)

    //   case 'std_dev':
    //     const avg = validData.reduce((acc, val) => acc + val, 0) / validData.length
    //     const squaredDiffs = validData.map(val => Math.pow(val - avg, 2))
    //     const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / validData.length
    //     return Math.sqrt(variance)

    //   case 'variance':
    //     const mean = validData.reduce((acc, val) => acc + val, 0) / validData.length
    //     const squaredDifferences = validData.map(val => Math.pow(val - mean, 2))
    //     return squaredDifferences.reduce((acc, val) => acc + val, 0) / validData.length

    //   case 'first':
    //     return validData[0]

    //   case 'last':
    //     return validData[validData.length - 1]

    //   default:
    //     return validData.reduce((acc, val) => acc + val, 0) / validData.length
    // }
  }
}

// ========================================
// AGRÉGATIONS TEMPORELLES
// ========================================

export class TemporalAggregator {

  static getGroupingKey(key: string, aggregation: TimeAggregation): string {

    switch (aggregation) {
      case 'hour':
        const hdate = new Date(key)
        return `${hdate.getFullYear()}-${String(hdate.getMonth() + 1).padStart(2, '0')}-${String(hdate.getDate()).padStart(2, '0')} ${String(hdate.getHours()).padStart(2, '0')}:00`

      case 'day':
        const ddate = new Date(key)
        return `${ddate.getDate().toString().padStart(2, '0')}`
      case 'week':
        return key

      case 'month':
        return key

      case 'year':
        return key

      case 'hour_distribution':
        return key.split(":")[0];

      case 'weekday':
        let weekdaydate = DateTime.now();
        weekdaydate = weekdaydate.startOf('week', {
          useLocaleWeeks: true
        });

        weekdaydate = weekdaydate.plus({ days: parseInt(key) - 1 });
        return new Intl.DateTimeFormat('fr-FR', {
          weekday: 'short',
        }).format(weekdaydate.toJSDate()) as string;

      case 'month_day':
        const monthdaydate = new Date(key);
        if (isNaN(monthdaydate.getTime())) {
          return key;
        }
        return `Jour ${monthdaydate.getDate()}`

      default:
        const date = new Date(key);
        if (isNaN(date.getTime())) {
          return key;
        }
        return date.toISOString().split('T')[0]
    }
  }

  static getDisplayLabel(key: string, aggregation: TimeAggregation): string {
    switch (aggregation) {
      case 'hour':
        let hdate = new Date(key);
        if (isNaN(hdate.getTime())) {
          return key;
        }
        return new Intl.DateTimeFormat('fr-FR', {
          hour: '2-digit',
        }).format(hdate)
      case 'day':
        let date = new Date();
        date.setDate(parseInt(key));
        if (isNaN(date.getTime())) {
          return key;
        }
        return new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
        }).format(date)

      case 'month':
        let d = new Date(key)
        if (isNaN(d.getTime())) {
          return key; // Retourner la clé originale si la date n'est pas valide
        }

        return new Intl.DateTimeFormat('fr-FR', {
          month: 'short'
        }).format(d)

      case 'year':
        let ydate = new Date();
        ydate.setFullYear(parseInt(key));
        if (isNaN(ydate.getTime())) {
          return key;
        }
        return new Intl.DateTimeFormat('fr-FR', {
          year: 'numeric',
        }).format(ydate)


      case 'hour_distribution':
        let hddate = new Date();
        hddate.setHours(parseInt(key));
        return new Intl.DateTimeFormat('fr-FR', {
          hour: '2-digit',
        }).format(hddate)

      case 'weekday':
        return key;
      case 'month_day':
      case 'week':
        let keyValues = key.split('-');
        let displayedWeek = DateTime.now().set({
          year: parseInt(keyValues[0]),
        });

        let weekStart = displayedWeek.set({
          weekYear: parseInt(keyValues[1]),
          weekday: 1
        })

        let weekEnd = weekStart.endOf('week');

        let weekstartString = new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
        }).format(weekStart.toJSDate())
        let weekendString = new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
        }).format(weekEnd.toJSDate())

        return `${weekstartString} - ${weekendString}`
      default:
        return key
    }
  }
}

// ========================================
// TRANSFORMATEUR PRINCIPAL
// ========================================

export class MobileDataTransformer {

  // Limite pour performance mobile
  // private readonly MOBILE_DATA_LIMIT = 200
  private readonly MOBILE_POINT_LIMIT = 100

  constructor(
    private isMobile: boolean = false,
    private compactMode: boolean = false
  ) { }

  /**
   * Transforme les données selon la configuration du graphique
   */
  async transform(
    rawData: RawDataPoint[],
    config: FlexibleChartConfig
  ): Promise<ChartDataset[]> {

    // Pas de limites mobiles pour avoir toutes les données
    const processedData = rawData

    const datasets: ChartDataset[] = []

    // Séparer les données principales et de comparaison
    const mainData = processedData.filter(item => !item.periodType || item.periodType === 'main')
    const comparisonData = processedData.filter(item => item.periodType === 'comparison')

    // Si comparaison activée, traiter les deux séries ensemble
    if (config.showComparison && comparisonData.length > 0) {
      // Créer les deux séries pour chaque dataSource
      for (const dataSource of config.dataSources) {
        // Série de comparaison D'ABORD pour qu'elle apparaisse à gauche
        const comparisonDataset = await this.transformDataSource(
          comparisonData,
          dataSource,
          config,
          true
        )

        if (comparisonDataset) {
          comparisonDataset.label = `${dataSource.name} (comparaison)`
          const borderColor = Array.isArray(comparisonDataset.borderColor)
            ? comparisonDataset.borderColor[0]
            : comparisonDataset.borderColor
          comparisonDataset.borderColor = this.adjustColorForComparison(borderColor)
          comparisonDataset.backgroundColor = this.adjustColorForComparison(borderColor)
          datasets.push(comparisonDataset)
        }

        // Série principale ENSUITE
        const mainDataset = await this.transformDataSource(
          mainData,
          dataSource,
          config,
          false
        )

        if (mainDataset) {
          datasets.push(mainDataset)
        }
      }
    } else {
      // Pas de comparaison, traitement normal
      for (const dataSource of config.dataSources) {
        const dataset = await this.transformDataSource(
          mainData,
          dataSource,
          config,
          false
        )

        if (dataset) {
          datasets.push(dataset)
        }
      }
    }

    return datasets
  }

  /**
   * Transforme une source de données spécifique
   */
  private async transformDataSource(
    rawData: RawDataPoint[],
    dataSource: DataSourceConfig,
    config: FlexibleChartConfig,
    isComparison: boolean = false
  ): Promise<ChartDataset | null> {

    // Filtrer les données selon la source
    let filteredData = this.filterByDataSource(rawData, dataSource)

    // Appliquer les filtres additionnels
    // filteredData = this.applyFilters(filteredData, dataSource.filters || [])

    // Appliquer l'agrégation temporelle si nécessaire
    let aggregatedData: TransformedDataPoint[]

    if (config.xAxis.type === 'time' && config.xAxis.timeAggregation) {
      aggregatedData = this.applyTemporalAggregation(
        filteredData,
        dataSource,
        config.xAxis.timeAggregation,
        isComparison
      )
    } else {
      // Agrégation simple
      aggregatedData = this.applySimpleAggregation(filteredData, dataSource)
    }

    // Appliquer les limites finales pour mobile
    if (this.isMobile && aggregatedData.length > this.MOBILE_POINT_LIMIT) {
      aggregatedData = this.reduceDataForMobile(aggregatedData, dataSource.aggregation)
    }

    // Créer le dataset final
    return this.createDataset(aggregatedData, dataSource, config.chartType)
  }

  /**
   * Applique les limites mobiles aux données brutes
   */
  // private applyMobileLimits(rawData: RawDataPoint[]): RawDataPoint[] {
  //   if (!this.isMobile || rawData.length <= this.MOBILE_DATA_LIMIT) {
  //     return rawData
  //   }

  //   // Séparer les données principales et de comparaison
  //   const mainData = rawData.filter(item => !item.periodType || item.periodType === 'main')
  //   const comparisonData = rawData.filter(item => item.periodType === 'comparison')

  //   // Si on a des données de comparaison, équilibrer les deux
  //   if (comparisonData.length > 0) {
  //     const halfLimit = Math.floor(this.MOBILE_DATA_LIMIT / 2)

  //     // Garder les plus récentes de chaque type
  //     const mainSorted = mainData
  //       .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  //       .slice(0, halfLimit)

  //     const comparisonSorted = comparisonData
  //       .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  //       .slice(0, halfLimit)

  //     return [...mainSorted, ...comparisonSorted]
  //   }

  //   // Pas de comparaison, comportement normal
  //   const sorted = rawData.sort((a, b) =>
  //     new Date(b.date).getTime() - new Date(a.date).getTime()
  //   )

  //   return sorted.slice(0, this.MOBILE_DATA_LIMIT)
  // }

  /**
   * Filtre les données par type de source
   */
  private filterByDataSource(
    rawData: RawDataPoint[],
    dataSource: DataSourceConfig
  ): RawDataPoint[] {
    // Pour l'instant, on retourne toutes les données
    // À implémenter selon la logique métier de chaque source
    return rawData.filter(item => {
      // Logique de filtrage basique
      switch (dataSource.source) {
        case 'products':
          return !!item.product_name
        case 'sales':
          return !!item.excluded_tax || !!item.included_tax
        default:
          return true
      }
    })
  }

  /**
   * Applique les filtres additionnels
   */
  // private applyFilters(
  //   data: RawDataPoint[],
  //   filters: any[]
  // ): RawDataPoint[] {
  //   // Implémentation des filtres à développer
  //   return data
  // }

  /**
   * Applique l'agrégation temporelle
   */
  private applyTemporalAggregation(
    data: RawDataPoint[],
    dataSource: DataSourceConfig,
    timeAggregation: TimeAggregation,
    isComparison: boolean = false
  ): TransformedDataPoint[] {

    // Grouper les données par période temporelle
    const groups = this.groupByTimeAggregation(data, timeAggregation)
    const result: TransformedDataPoint[] = []
    
    // Traiter chaque groupe
    for (const [groupKey, groupData] of groups.entries()) {
      const values = groupData.map(item => this.extractFieldValue(item, dataSource.field))
      const aggregatedValue = StatisticalAggregator.aggregate(values, dataSource.aggregation)
      // Pour les axes temporels, utiliser la date brute, sinon utiliser le label
      let xValue = ['day', 'month', 'year', 'hour'].includes(timeAggregation)
      ? groupKey
      : TemporalAggregator.getDisplayLabel(groupKey, timeAggregation)

      // Pour les barres, pas de décalage - même position X pour comparaison et principale
      // Pour les courbes, décalage léger pour éviter le chevauchement
      // Note: le type de graphique n'est pas disponible ici, on supprime le décalage pour l'instant

      result.push({
        x: xValue,
        y: aggregatedValue,
        label: TemporalAggregator.getDisplayLabel(groupKey, timeAggregation),
        metadata: {
          originalCount: groupData.length,
          groupKey,
          timeAggregation,
          periodType: isComparison ? 'comparison' : 'main'
        }
      })
    }

    // Trier par ordre temporel  
    const sorted = this.sortTemporalData(result, timeAggregation)

    // Pour l'agrégation mensuelle, s'assurer que tous les mois sont présents
    if (timeAggregation === 'month') {
      const filledData = this.fillMissingMonths(sorted)
      return filledData
    }

    return sorted
  }

  /**
   * Complète les mois manquants avec des valeurs à 0
   */
  private fillMissingMonths(data: TransformedDataPoint[]): TransformedDataPoint[] {
    // Ne pas remplir automatiquement - laisser ECharts gérer l'affichage
    // Cela évite les problèmes de types mixtes

    // Simplement trier les données existantes par mois (groupKey)
    return data.sort((a, b) => {
      const aMonth = a.metadata?.groupKey || String(a.x)
      const bMonth = b.metadata?.groupKey || String(b.x)
      return aMonth.localeCompare(bMonth)
    })
  }

  /**
   * Groupe les données par agrégation temporelle
   */
  private groupByTimeAggregation(data: RawDataPoint[], timeAggregation: TimeAggregation): Map<string, RawDataPoint[]> {
    const groups = new Map<string, RawDataPoint[]>()

    for (const item of data) {
      // check if item.date is really a date, otherwise juste take the key as it is
      const groupKey = TemporalAggregator.getGroupingKey(item.date, timeAggregation)

      if (!groups.has(groupKey)) {
        groups.set(groupKey, [])
      }

      groups.get(groupKey)!.push(item)
    }

    return this.sortByTimeAggregation(groups, timeAggregation)
  }

  private sortByTimeAggregation(groups: Map<string, RawDataPoint[]>, timeAggregation: TimeAggregation): Map<string, RawDataPoint[]> {
    switch (timeAggregation) {
      case 'hour':
      case 'hour_distribution':
        return this.sortHours(groups)
      case 'weekday':
        return this.sortWeekdays(groups)
      default:
        return this.defaultSort(groups)
    }
  }

  private defaultSort(groups: Map<string, RawDataPoint[]>): Map<string, RawDataPoint[]> {
    // sort groups by keys
    const sortedGroups = new Map<string, RawDataPoint[]>()
    const keys = Array.from(groups.keys()).sort()

    for (const key of keys) {
      sortedGroups.set(key, groups.get(key)!)
    }

    return sortedGroups
  }

  private sortHours(groups: Map<string, RawDataPoint[]>): Map<string, RawDataPoint[]> {
    const sortedGroups = new Map<string, RawDataPoint[]>()


    const keys = Array.from(groups.keys()).sort()

    // Days start at 4am and ends at 3:59:59am
    // Extract hours between 0 and 3 included
    const nightHours = keys.filter((value: string) => parseInt(value) >= 0 && parseInt(value) <= 3)
    const dayHours = keys.filter((value: string) => parseInt(value) > 3)

    // Set the night hours after the day hours and sort the group based on this hours
    const sortedKeys = [
      ...dayHours,
      ...nightHours
    ]

    for (const key of sortedKeys) {
      sortedGroups.set(key, groups.get(key)!)
    }

    return sortedGroups;
  }

  private sortWeekdays(groups: Map<string, RawDataPoint[]>): Map<string, RawDataPoint[]> {
    const sortedGroups = new Map<string, RawDataPoint[]>()

    // Obtenir l'ordre des jours de la semaine selon la localisation
    const weekdayOrder = this.getWeekdayOrder()

    // Trier les clés selon l'ordre des weekdays de la localisation
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
      // Extraire le nom du jour depuis la clé (ex: "lun.", "mar.", etc.)
      // La clé est générée par TemporalAggregator.getGroupingKey()

      // Trouver les numéros de weekday correspondants en recréant les dates
      let weekdayA = 1, weekdayB = 1

      try {
        // Recréer les weekdays depuis les noms courts formatés
        for (let i = 1; i <= 7; i++) {
          const testDate = DateTime.now().startOf('week', { useLocaleWeeks: true }).plus({ days: i - 1 })
          const testKey = new Intl.DateTimeFormat(navigator.language, { weekday: 'short' }).format(testDate.toJSDate())

          if (testKey === a) weekdayA = testDate.weekday
          if (testKey === b) weekdayB = testDate.weekday
        }
      } catch (error) {
        console.warn('Erreur lors du tri des weekdays:', error)
      }

      // Comparer selon l'ordre défini par la localisation
      const indexA = weekdayOrder.indexOf(weekdayA)
      const indexB = weekdayOrder.indexOf(weekdayB)

      return (indexA !== -1 ? indexA : 999) - (indexB !== -1 ? indexB : 999)
    })

    // Reconstruire la Map avec l'ordre correct
    for (const key of sortedKeys) {
      sortedGroups.set(key, groups.get(key)!)
    }

    return sortedGroups
  }

  /**
   * Obtient l'ordre des jours de la semaine selon la localisation
   * Utilise Luxon pour déterminer l'ordre correct des weekdays
   */
  private getWeekdayOrder(): number[] {
    // Créer une date de référence (lundi d'une semaine quelconque)
    const monday = DateTime.now().startOf('week', { useLocaleWeeks: true })

    // Générer l'ordre des jours selon la localisation
    const weekdayOrder: number[] = []
    for (let i = 0; i < 7; i++) {
      const day = monday.plus({ days: i })
      weekdayOrder.push(day.weekday) // 1=lundi, 2=mardi, ..., 7=dimanche
    }

    return weekdayOrder
  }

  /**
   * Applique une agrégation simple (sans dimension temporelle)
   */
  private applySimpleAggregation(
    data: RawDataPoint[],
    dataSource: DataSourceConfig
  ): TransformedDataPoint[] {

    const values = data.map(item => this.extractFieldValue(item, dataSource.field))
    const aggregatedValue = StatisticalAggregator.aggregate(values, dataSource.aggregation)

    return [{
      x: dataSource.name,
      y: aggregatedValue,
      label: dataSource.name,
      metadata: {
        originalCount: data.length,
        aggregationType: dataSource.aggregation
      }
    }]
  }

  /**
   * Extrait la valeur d'un champ depuis un objet de données
   */
  private extractFieldValue(item: RawDataPoint, field: string): number {
    const value = item[field]
    return typeof value === 'number' ? value : 0
  }

  /**
   * Trie les données temporelles
   */
  private sortTemporalData(
    data: TransformedDataPoint[],
    timeAggregation: TimeAggregation
  ): TransformedDataPoint[] {

    // Pour certains types, l'ordre naturel est suffisant
    if (['hour_distribution', 'weekday', 'month_day'].includes(timeAggregation)) {
      return data
    }

    // Pour les autres, trier par x (qui représente la date/période)
    return data.sort((a, b) => {
      if (typeof a.x === 'string' && typeof b.x === 'string') {
        return a.x.localeCompare(b.x)
      }
      return 0
    })
  }

  /**
   * Réduit les données pour les performances mobile
   */
  private reduceDataForMobile(
    data: TransformedDataPoint[],
    _aggregationType: AggregationType
  ): TransformedDataPoint[] {

    // Pour les graphiques temporels, garder un point sur N
    const step = Math.ceil(data.length / this.MOBILE_POINT_LIMIT)
    const reduced: TransformedDataPoint[] = []

    for (let i = 0; i < data.length; i += step) {
      reduced.push(data[i])
    }

    return reduced
  }

  /**
   * Crée le dataset final pour Chart.js
   */
  private createDataset(
    data: TransformedDataPoint[],
    dataSource: DataSourceConfig,
    chartType?: string
  ): ChartDataset {
    
    // Pour les graphiques pie/doughnut, générer un array de couleurs
    let backgroundColor: string | string[]
    let borderColor: string | string[]
    
    if (chartType === 'pie' || chartType === 'doughnut') {
      // Générer des couleurs différentes pour chaque segment
      backgroundColor = data.map((_, index) => this.getColorForIndex(index))
      borderColor = backgroundColor
    } else {
      // Pour les autres types, utiliser une couleur unique
      backgroundColor = dataSource.color || this.getDefaultColor(dataSource.id)
      borderColor = backgroundColor
    }

    return {
      label: dataSource.name,
      data: data,
      backgroundColor,
      borderColor,
      borderWidth: this.isMobile ? 2 : 3,
      tension: 0.4,
      fill: false,
      // Optimisations mobile
      pointRadius: this.isMobile ? (this.compactMode ? 2 : 3) : 4,
      pointHoverRadius: this.isMobile ? 4 : 6,
      borderCapStyle: 'round'
    }
  }

  /**
   * Génère une couleur par défaut pour une source de données
   */
  private getDefaultColor(dataSourceId: string): string {
    const colors = [
      '#6366f1', // Indigo moderne
      '#ec4899', // Rose vibrant
      '#06b6d4', // Cyan élégant
      '#f59e0b', // Amber chaleureux
      '#8b5cf6', // Violet sophistiqué
      '#10b981', // Emerald frais
      '#f97316', // Orange énergique
      '#ef4444', // Rouge moderne
    ]

    // Hash simple pour associer un ID à une couleur
    let hash = 0
    for (let i = 0; i < dataSourceId.length; i++) {
      hash = dataSourceId.charCodeAt(i) + ((hash << 5) - hash)
    }

    return colors[Math.abs(hash) % colors.length]
  }

  /**
   * Génère une couleur basée sur l'index pour les graphiques pie/doughnut
   */
  private getColorForIndex(index: number): string {
    const pieColors = [
      '#6366f1', // Indigo moderne
      '#ec4899', // Rose vibrant
      '#06b6d4', // Cyan élégant
      '#f59e0b', // Amber chaleureux
      '#8b5cf6', // Violet sophistiqué
      '#10b981', // Emerald frais
      '#f97316', // Orange énergique
      '#ef4444', // Rouge moderne
      '#14b8a6', // Teal profond
      '#a855f7', // Pourpre intense
      '#22c55e', // Vert lime
      '#eab308', // Jaune doré
      '#f472b6', // Pink doux
      '#84cc16', // Lime électrique
      '#64748b', // Slate moderne
      '#0ea5e9', // Sky blue
      '#d946ef', // Fuchsia
      '#059669', // Emerald foncé
      '#dc2626', // Rouge intense
      '#7c3aed'  // Violet royal
    ]

    return pieColors[index % pieColors.length]
  }

  /**
   * Génère un dégradé moderne pour les graphiques
   */
  // private getModernGradient(baseColor: string): any {
  //   // Convertir la couleur hex en RGB pour créer des variants
  //   const r = parseInt(baseColor.slice(1, 3), 16)
  //   const g = parseInt(baseColor.slice(3, 5), 16)
  //   const b = parseInt(baseColor.slice(5, 7), 16)
    
  //   // Créer une version plus claire pour le dégradé
  //   const lighterColor = `rgba(${Math.min(255, r + 40)}, ${Math.min(255, g + 40)}, ${Math.min(255, b + 40)}, 0.8)`
  //   const baseColorRgba = `rgba(${r}, ${g}, ${b}, 1)`
    
  //   return {
  //     type: 'linear',
  //     x: 0,
  //     y: 0,
  //     x2: 0,
  //     y2: 1,
  //     colorStops: [
  //       { offset: 0, color: lighterColor },
  //       { offset: 1, color: baseColorRgba }
  //     ]
  //   }
  // }

  /**
   * Ajuste la couleur pour la série de comparaison (déclinaison plus claire de la couleur principale)
   */
  private adjustColorForComparison(color?: string): string {
    if (!color) return 'rgba(148, 163, 184, 0.25)' // Gris par défaut

    // Si c'est déjà en format rgba, extraire les valeurs RGB
    if (color.startsWith('rgba(') || color.startsWith('rgb(')) {
      const values = color.match(/\d+/g)
      if (values && values.length >= 3) {
        const r = parseInt(values[0])
        const g = parseInt(values[1]) 
        const b = parseInt(values[2])
        return `rgba(${r}, ${g}, ${b}, 0.25)`
      }
    }

    // Si c'est en format hex
    if (color.startsWith('#') && color.length === 7) {
      const hex = color.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      return `rgba(${r}, ${g}, ${b}, 0.5)`
    }

    // Fallback pour autres formats
    return 'rgba(148, 163, 184, 0.25)'
  }
}

// ========================================
// UTILITAIRES SUPPLÉMENTAIRES
// ========================================

/**
 * Génère des données de test réalistes pour le développement
 */
export function generateMockData(config: FlexibleChartConfig): RawDataPoint[] {
  const data: RawDataPoint[] = []

  // Générer des données pour la période principale
  generateDataForPeriod(config, config.period.start, config.period.end, data)

  // Si comparaison activée, générer des données pour la période de comparaison
  if (config.showComparison && config.comparisonPeriod) {
    generateDataForPeriod(config, config.comparisonPeriod.start, config.comparisonPeriod.end, data, true)
  } else {
    console.log('No comparison data generated because:', {
      showComparison: config.showComparison,
      hasComparisonPeriod: !!config.comparisonPeriod
    })
  }

  return data
}

/**
 * Génère des données pour une période donnée
 */
function generateDataForPeriod(
  config: FlexibleChartConfig,
  startDateStr: string,
  endDateStr: string,
  data: RawDataPoint[],
  isComparison: boolean = false
): RawDataPoint[] {
  const startDate = new Date(startDateStr)
  const endDate = new Date(endDateStr)
  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

  // Définir les patterns de données réalistes
  const families = ['Karting', 'Laser Game', 'Arcade', 'Restaurant', 'Boutique']
  const products = [
    'Session 10min', 'Session 20min', 'Session 30min', 'Package Groupe',
    'Laser Tag Battle', 'Laser Tag Tournament', 'Arcade Tokens', 'Boisson', 'Snack'
  ]
  const productPrices = [15, 25, 35, 120, 12, 18, 5, 3.5, 4.5]

  // Générer des patterns saisonniers et hebdomadaires
  const baselineMultiplier = getBusinesSMultiplier(config.chartType)

  // Facteur pour la période de comparaison (généralement un peu différent)
  const comparisonMultiplier = isComparison ? (0.85 + Math.random() * 0.3) : 1.0 // 85%-115% des valeurs principales

  for (let i = 0; i <= daysDiff; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)

    // Facteurs réalistes selon le jour de la semaine
    const dayOfWeek = date.getDay()
    const weekendBoost = [0, 6].includes(dayOfWeek) ? 1.8 : 1.0 // Weekend boost
    const weekdayMultiplier = [3, 4, 5].includes(dayOfWeek) ? 1.3 : 1.0 // Mercredi-Vendredi boost

    // Facteur saisonnier (plus d'activité en été, vacances)
    const month = date.getMonth()
    const seasonalBoost = [5, 6, 7, 11, 0].includes(month) ? 1.4 : 1.0 // Été + hiver

    // Générer plusieurs transactions par jour
    const transactionsPerDay = Math.floor(Math.random() * 8 + 2) * weekendBoost

    for (let t = 0; t < transactionsPerDay; t++) {
      const familyIndex = Math.floor(Math.random() * families.length)
      const productIndex = Math.floor(Math.random() * products.length)

      // Simulation horaire réaliste (10h-22h avec pics)
      const hour = generateRealisticHour()
      const transactionDate = new Date(date)
      transactionDate.setHours(hour, Math.floor(Math.random() * 60))

      // Calculs réalistes basés sur le type de données demandé
      const basePrice = productPrices[productIndex]
      const quantity = Math.floor(Math.random() * 4) + 1
      const totalAmount = basePrice * quantity * weekdayMultiplier * seasonalBoost * baselineMultiplier * comparisonMultiplier

      // Calculs financiers réalistes
      const taxRate = 0.20 // TVA 20%
      const excludedTaxAmount = Math.round(totalAmount / (1 + taxRate) * 100) / 100
      const taxAmount = Math.round((totalAmount - excludedTaxAmount) * 100) / 100
      const totalPaid = totalAmount * (0.95 + Math.random() * 0.1) // Légers écarts de paiement

      data.push({
        date: transactionDate.toISOString(),

        // ===== MÉTADONNÉES DE PÉRIODE =====
        periodType: isComparison ? 'comparison' : 'main',
        periodLabel: isComparison ? 'Période de comparaison' : 'Période principale',

        // ===== CHAMPS SALES (définis dans SALES_FIELDS) =====
        excluded_tax: excludedTaxAmount,
        included_tax: totalAmount,
        total_paid: Math.round(totalPaid * 100) / 100,
        total_documents: 1,
        average_transaction: totalAmount,
        tax_amount: taxAmount,

        // ===== CHAMPS PRODUCTS (définis dans PRODUCTS_FIELDS) =====  
        product_name: products[productIndex],
        family_name: families[familyIndex],
        line_count: quantity,
        average_sale_value: Math.round((totalAmount / quantity) * 100) / 100,
        market_share_revenue: Math.random() * 15 + 5, // 5-20%
        market_share_volume: Math.random() * 12 + 3, // 3-15%
        tax_efficiency: Math.random() * 20 + 80, // 80-100
        volume_efficiency: Math.random() * 25 + 75, // 75-100
        frequency_score: Math.random() * 30 + 70, // 70-100
        rank_in_family: Math.floor(Math.random() * 10) + 1, // 1-10
        product_contribution_pct: Math.random() * 40 + 10, // 10-50%
        top_performer: Math.random() > 0.8,

        // ===== CHAMPS SUPPLÉMENTAIRES POUR ENRICHISSEMENT =====
        hour: hour,
        dayOfWeek: dayOfWeek,
        month: month,
        isWeekend: [0, 6].includes(dayOfWeek),
        customerAge: Math.floor(Math.random() * 50) + 15,
        customerType: Math.random() > 0.7 ? 'member' : 'casual',
        paymentMethod: Math.random() > 0.3 ? 'card' : 'cash',
        groupSize: Math.floor(Math.random() * 6) + 1,

        // Métriques de performance
        sessionDuration: Math.floor(Math.random() * 45) + 15,
        satisfaction: Math.random() * 2 + 3,
        repeatCustomer: Math.random() > 0.6,

        // Géographie
        city: ['Paris', 'Lyon', 'Marseille', 'Local'][Math.floor(Math.random() * 4)],
        postalCode: `${Math.floor(Math.random() * 90000) + 10000}`,

        // Saisonnalité
        temperature: 15 + Math.random() * 20,
        isHoliday: Math.random() > 0.9,
        schoolHolidays: isSchoolHolidays(date),

        // KPIs métier
        utilizationRate: Math.random() * 0.4 + 0.6,
        averageSpentPerCustomer: totalAmount / Math.max(1, Math.floor(Math.random() * 3) + 1),
        conversionRate: Math.random() * 0.3 + 0.4
      })
    }
  }

  return data
}

/**
 * Génère une heure réaliste d'activité (10h-22h avec pics)
 */
function generateRealisticHour(): number {
  const hourWeights = [
    0.1, 0.1, 0.2, 0.4, 0.6, 0.8, 1.0, 0.9, // 10h-17h
    1.2, 1.5, 1.3, 1.0, 0.7 // 18h-22h (pics soirée)
  ]

  const totalWeight = hourWeights.reduce((sum, w) => sum + w, 0)
  let random = Math.random() * totalWeight

  for (let i = 0; i < hourWeights.length; i++) {
    random -= hourWeights[i]
    if (random <= 0) {
      return 10 + i // Heure de 10h à 22h
    }
  }

  return 14 // Heure par défaut
}

/**
 * Détermine si une date est pendant les vacances scolaires
 */
function isSchoolHolidays(date: Date): boolean {
  const month = date.getMonth()
  const day = date.getDate()

  // Périodes approximatives de vacances
  return (
    (month === 6 || month === 7) || // Juillet-Août
    (month === 11 && day > 20) ||   // Fin décembre
    (month === 0 && day < 8) ||     // Début janvier
    (month === 1 && day > 15 && day < 25) || // Février
    (month === 3 && day > 15 && day < 30)    // Avril
  )
}

/**
 * Retourne un multiplicateur de base selon le type de graphique
 */
function getBusinesSMultiplier(chartType: string): number {
  switch (chartType) {
    case 'pie':
    case 'doughnut':
      return 1.2 // Données plus concentrées pour les camemberts
    case 'bar':
      return 1.0 // Données standard
    case 'line':
    case 'area':
      return 0.8 // Données plus étalées pour montrer les tendances
    default:
      return 1.0
  }
}

// ========================================
// CONVERSION DES DONNÉES API
// ========================================

/**
 * Convertit une réponse SalesByIntervalResponse en format RawDataPoint
 */
export function convertSalesApiToRawData(
  salesResponse: SalesByIntervalResponse,
  comparisonSalesResponse?: SalesByIntervalResponse
): RawDataPoint[] {
  const data: RawDataPoint[] = []

  // Convertir les données principales
  if (salesResponse.intervals) {
    salesResponse.intervals.forEach(interval => {
      data.push({
        date: interval.date,
        periodType: 'main',
        periodLabel: 'Période principale',

        // Champs SALES depuis PeriodStatistics
        excluded_tax: interval.data.period.tax_data.excluded_tax,
        included_tax: interval.data.period.tax_data.included_tax,
        total_paid: interval.data.period.total_paid,
        total_documents: interval.data.period.total_documents,
        average_transaction: interval.data.period.total_documents > 0
          ? interval.data.period.tax_data.excluded_tax / interval.data.period.total_documents
          : 0,
        tax_amount: interval.data.period.tax_data.included_tax - interval.data.period.tax_data.excluded_tax
      })
    })
  }

  // Convertir les données de comparaison si présentes
  if (comparisonSalesResponse?.intervals) {
    comparisonSalesResponse.intervals.forEach(interval => {
      data.push({
        date: interval.date,
        periodType: 'comparison',
        periodLabel: 'Période de comparaison',

        // Champs SALES depuis PeriodStatistics  
        excluded_tax: interval.data.period.tax_data.excluded_tax,
        included_tax: interval.data.period.tax_data.included_tax,
        total_paid: interval.data.period.total_paid,
        total_documents: interval.data.period.total_documents,
        average_transaction: interval.data.period.total_documents > 0
          ? interval.data.period.tax_data.excluded_tax / interval.data.period.total_documents
          : 0,
        tax_amount: interval.data.period.tax_data.included_tax - interval.data.period.tax_data.excluded_tax
      })
    })
  }

  return data
}

/**
 * Convertit une réponse ProductsReportResponse en format RawDataPoint
 */
export function convertProductsApiToRawData(
  productsResponse: ProductsReportResponse,
  comparisonProductsResponse?: ProductsReportResponse
): RawDataPoint[] {
  const data: RawDataPoint[] = []

  // Convertir les données principales
  if (productsResponse.products) {
    productsResponse.products.forEach(product => {
      data.push({
        date: productsResponse.period.start, // Les produits sont agrégés sur toute la période
        periodType: 'main',
        periodLabel: 'Période principale',

        // Champs PRODUCTS depuis EnrichedProduct - Structure corrigée
        product_id: product.product_data.product_id,
        product_name: product.product_data.product_name,
        family_name: product.product_data.family_name,
        excluded_tax: product.product_data.tax_data.excluded_tax,
        included_tax: product.product_data.tax_data.included_tax,
        line_count: product.product_data.line_count,
        average_sale_value: product.product_data.average_sale_value,
        market_share_revenue: product.product_data.market_share_revenue,
        market_share_volume: product.product_data.market_share_volume,
        tax_efficiency: product.performance.profitability_metrics?.tax_efficiency || 0,
        volume_efficiency: product.performance.profitability_metrics?.volume_efficiency || 0,
        rank_in_family: product.performance.family_performance?.rank_in_family || 0,
        product_contribution_pct: product.performance.family_performance?.product_contribution_pct || 0,
        top_performer: product.performance.top_performer || false
      })
    })
  }

  // Convertir les données de comparaison si présentes
  if (comparisonProductsResponse?.products) {
    comparisonProductsResponse.products.forEach(product => {
      data.push({
        date: comparisonProductsResponse.period.start,
        periodType: 'comparison',
        periodLabel: 'Période de comparaison',

        // Champs PRODUCTS depuis EnrichedProduct - Structure corrigée
        product_id: product.product_data.product_id,
        product_name: product.product_data.product_name,
        family_name: product.product_data.family_name,
        excluded_tax: product.product_data.tax_data.excluded_tax,
        included_tax: product.product_data.tax_data.included_tax,
        line_count: product.product_data.line_count,
        average_sale_value: product.product_data.average_sale_value,
        market_share_revenue: product.product_data.market_share_revenue,
        market_share_volume: product.product_data.market_share_volume,
        tax_efficiency: product.performance.profitability_metrics?.tax_efficiency || 0,
        volume_efficiency: product.performance.profitability_metrics?.volume_efficiency || 0,
        rank_in_family: product.performance.family_performance?.rank_in_family || 0,
        product_contribution_pct: product.performance.family_performance?.product_contribution_pct || 0,
        top_performer: product.performance.top_performer || false
      })
    })
  }

  return data
}